import moment from 'moment'

const DAILY = ['grid', 'list', 'day']
const MONTHLY = ['month']
const WEEKLY = ['agendaWeek', 'week']

export default {
  /**
   * Gives us a list of dates based on display type and formats
   * @param {*} from Start date
   * @param {*} to End date
   * @param {*} type What display type
   * @param {*} format Primary format
   * @param {*} formatAlt Secondary format (mostly for agenda week)
   * @returns {*} a list of generated dates
   */
  getDateList (from, to, type) {
    from = removeTime(from)
    to = removeTime(to)

    let { format, formatAlt } = getDateFormat(type)

    if (isDaily(type)) {
      return makeDayDates(from, to, format)
    } else if (isWeekly(type)) {
      return makeWeekDates(from, to, format, formatAlt)
    } else if (isMonthly(type)) {
      return makeMonthDates(from, to, format)
    }
  },

  /**
   * Gets initial date filters for display grids
   * @param {String} style Display style we are using
   * @returns {Object} Date filter object
   */
  getInitialDateFilter (style, now = null) {
    // Date filter object
    let date = {}

    // Based on style prepare initial filter
    let base = null
    if (now !== null) {
      base = moment(now).toISOString()
    } else {
      base = moment().toISOString()
    }

    if (isDaily(style)) {
      date = {
        id: moment(base).startOf('day').toISOString(),
        from: moment(base).startOf('week').toISOString(),
        // to: moment(base).startOf('day').add(1, 'd').toISOString()
        to: moment(base).endOf('week').toISOString()
      }
    } else if (isWeekly(style)) {
      date = {
        id: moment(base).startOf('week').toISOString(),
        from: moment(base).startOf('week').toISOString(),
        to: moment(base).endOf('week').toISOString()
      }
    } else if (isMonthly(style)) {
      date = {
        id: moment(base).startOf('month').toISOString(),
        from: moment(base).startOf('month').toISOString(),
        to: moment(base).endOf('month').toISOString()
      }
    }

    return date
  },

  /**
   * Adjusts range so first week fits inside the month
   * @param {String} from From date
   * @param {String} to To date
   */
  correctMonthToWeekRange (from, to) {
    // If from/to month's don't match we missed it
    if (moment(from).format('M') !== moment(to).format('M')) {
      from = moment(from).add(1, 'week').toISOString()
      to = moment(from).endOf('week').toISOString()
    }

    return { from, to }
  }
}

/**
 * Gives format to be used for the given date step
 * @param {String} dateStep 
 * @returns {Object}
 */
function getDateFormat (dateStep) {
  let format = null
  let formatAlt = null

  switch (dateStep) {
    case 'agendaWeek':
      format = 'DD'
      formatAlt = 'DD MMM. YY'
      break;

    case 'week':
      format = 'DD'
      formatAlt = 'DD MMM. YY'
      break;

    case 'month':
      format = 'MMM YY'
      break;

    default:
      format = 'D. MMM. YY'
  }

  return { format, formatAlt }
}

/**
 * Tells if the selected style requires monthly dates
 * @param {String} style  Display style to use
 */
function isMonthly (style) {
  return MONTHLY.indexOf(style) > -1
}

/**
 * Tells if the selected style requires weekly dates
 * @param {String} style  Display style to use
 */
function isWeekly (style) {
  return WEEKLY.indexOf(style) > -1
}

/**
 * Tells if the selected style requires daily dates
 * @param {String} style  Display style to use
 */
function isDaily (style) {
  return DAILY.indexOf(style) > -1
}

/**
 * Generates dates with a day step
 * @param {*} from Start date
 * @param {*} to End date
 * @param {*} format Format to use
 * @returns {*} A list of day stepped dates
 */
function makeDayDates (from, to, format) {
  let today = moment(from)
  to = moment(to)
  let rtr = []

  while (today.isSameOrBefore(to)) {
    rtr.push({
      id: today.toISOString(),
      name: moment(today.toISOString()).format(format),
      extraFields: {
        from: moment(today.toISOString()).toISOString(),
        to: moment(today.toISOString()).add(1, 'days').toISOString()
      }
    })

    today = today.add(1, 'days')
  }

  return rtr
}

/**
 * Generates dates with a week step
 * @param {*} from Start date - Included
 * @param {*} to End date - Included
 * @param {*} format Format to use
 * @param {*} formatAlt Alt format to use (Second part of the date)
 * @returns {*} A list of week stepped dates
 */
function makeWeekDates (from, to, format, formatAlt) {
  let today = moment(from).startOf('week')
  to = moment(to)
  let rtr = []

  while (today.isSameOrBefore(to)) {
    rtr.push({
      id: today.toISOString(),
      name: `${moment(today.toISOString()).format(format)} - ${moment(today.toISOString()).add(1, 'w').add(-1, 'd').format(formatAlt)}`,
      extraFields: {
        from: moment(today.toISOString()).toISOString(),
        to: moment(today.toISOString()).add(1, 'w').toISOString()
      }
    })

    today = today.add(1, 'w')
  }

  return rtr
}

/**
 * Generates dates with a month step
 * @param {*} from Start date
 * @param {*} to End date
 * @param {*} format Format to use
 * @returns {*} A list of month stepped dates
 */
function makeMonthDates (from, to, format) {
  let today = moment(from).startOf('month')
  to = moment(to).endOf('month')
  let rtr = []

  while (today.isSameOrBefore(to)) {
    rtr.push({
      id: today.toISOString(),
      name: today.format(format),
      extraFields: {
        from: today.toISOString(),
        to: moment(today).add(1, 'M').toISOString()
      }
    })

    today = today.add(1, 'M')
  }

  return rtr
}

/**
 * Removes the time by setting it to 0:00
 * @param {*} date A date to remove the time from
 * @returns {*} Timeless date
 */
function removeTime (date) {
  date = moment(date)
  date.set({
    hours: 0,
    minutes: 0,
    seconds: 0
  })

  return date.toISOString()
}



// WEBPACK FOOTER //
// ./src/assets/modules/displayGrids/makeDates.js